'use client';

import {
  ApplicationNetworkingSection,
  SettingsCard,
  useInternalApplicationDraft,
  type GlobalHeaderSource,
} from '@terra/ui-design-system';
import { useApplicationById } from '../../../hooks/api/useApplicationById';
import {
  useApplicationInternalAccessSettings,
  useTargetConnectors,
  useTargetTwingateNetworks,
} from '../../../hooks/api/useApplicationNetworkingSettings';
import { useInternalAccessNetworks } from '../../../hooks/api/useInternalAccessNetworks';
import { useUpdateApplicationFields } from '../../../hooks/api/useUpdateApplicationFields';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { useOpenWebAppPage } from '../../../hooks/useOpenWebAppPage';
import { WEB_APP_PATHS } from '../../../utils/webAppPaths';

type TargetNetworkingPanelProps = {
  applicationId: string;
  isReadOnly: boolean;
};

export function TargetNetworkingPanel({
  applicationId,
  isReadOnly,
}: TargetNetworkingPanelProps) {
  const { application, isLoading: isApplicationLoading } = useApplicationById({
    id: applicationId,
  });

  if (isApplicationLoading || !application) {
    return <SettingsCard isLoading>{null}</SettingsCard>;
  }

  return (
    <TargetNetworkingContent
      key={applicationId}
      applicationId={applicationId}
      isReadOnly={isReadOnly}
      isPersistedInternalApp={application.isInternalApp ?? false}
      globalHeaders={application.globalHeaders}
      limitGlobalHeadersByScope={application.limitGlobalHeadersByScope ?? false}
    />
  );
}

function TargetNetworkingContent({
  applicationId,
  isReadOnly,
  isPersistedInternalApp,
  globalHeaders,
  limitGlobalHeadersByScope,
}: TargetNetworkingPanelProps & {
  isPersistedInternalApp: boolean;
  globalHeaders: readonly GlobalHeaderSource[];
  limitGlobalHeadersByScope: boolean;
}) {
  const openWebAppPage = useOpenWebAppPage();
  const draft = useInternalApplicationDraft(isPersistedInternalApp);
  const {
    isFeatureFlagEnabled: isInternalAccessEnabled,
    isFetched: areFeatureFlagsResolved,
  } = useFeatureFlags('TAILSCALE_INTERNAL_APP_ENABLED');
  const { isFeatureFlagEnabled: isTwingateEnabled } = useFeatureFlags(
    'TWINGATE_CONNECTORS_ENABLED',
  );
  const { networks: twingateNetworks, isLoading: isTwingateLoading } =
    useTargetTwingateNetworks({ enabled: true });
  const {
    networks: internalAccessNetworks,
    isLoading: isInternalAccessNetworksLoading,
  } = useInternalAccessNetworks({ enabled: isInternalAccessEnabled });
  const { connectors, isLoading: isConnectorsLoading } = useTargetConnectors({
    enabled: true,
  });
  const {
    internalAccess,
    isLoading: isInternalAccessLoading,
    upsertBinding,
    testConnection,
  } = useApplicationInternalAccessSettings({
    applicationId,
    enabled: draft.isInternalAccessQueryEnabled,
  });
  const updateApplication = useUpdateApplicationFields();

  return (
    <ApplicationNetworkingSection
      isReadOnly={isReadOnly}
      internalAccessSettings={{
        draft,
        inventory: {
          twingateNetworks,
          internalAccessNetworks,
          connectors,
          flags: { isInternalAccessEnabled, isTwingateEnabled },
          areFeatureFlagsResolved,
          isTwingateLoading,
          isInternalAccessNetworksLoading:
            isInternalAccessEnabled && isInternalAccessNetworksLoading,
          isConnectorsLoading,
        },
        internalAccess,
        isInternalAccessLoading,
        isSavingBinding: upsertBinding.isPending,
        onSaveBinding: upsertBinding.mutateAsync,
        onTestConnection: () => testConnection.mutateAsync(),
        onManageNetworks: () => openWebAppPage(WEB_APP_PATHS.manageNetworks),
      }}
      globalHeaders={{
        globalHeaders,
        limitGlobalHeadersByScope,
        isSaving: updateApplication.isPending,
        onSave: (body) =>
          updateApplication.mutateAsync({ applicationId, body }),
      }}
    />
  );
}
